/**
 * The shared thunk body for the ATS Redux slices.
 *
 * Five slices (atsJobCreate, atsJobs — since moved to TanStack, atsJobDetails,
 * atsJobPipeline, referredCandidates) each carried their own copy of this function. The copies
 * drifted: only atsJobCreate ever got the 4xx guard below, so a 403's `data: {}`
 * kept landing in the other four slices' `data` and crashing consumers that
 * expected an array — JobDashboard's PipelineList/ApplicationChart and
 * JobPipeline in particular.
 *
 * Per-slice differences are options rather than forks, so adopting this changes
 * nothing for a slice beyond gaining the guard.
 */

/**
 * Every internal API response is the Rails `api_v1.json.erb` envelope. `data` is
 * present on failure too (usually `{}`), which is why a `= []` default at the
 * call site never fires — errors are signalled by `errors`/`status`, not by a
 * missing `data`. `status` is injected by the auth interceptor on 4xx only.
 */
export interface ApiEnvelope {
  data?: unknown;
  errors?: string[];
  meta?: Record<string, unknown>;
  flash?: unknown;
  status?: number;
}

export interface ApiActionConfig {
  endpoint: string;
  method: string;
  reducerName: string;
  /** Present on reads; its absence marks a mutation whose caller reads the envelope. */
  responseKey?: string;
  thunkName?: string;
}

export interface ApiRequestOptions {
  requestConfig?: Record<string, unknown>;
  isBackground?: boolean;
  pathParams?: Record<string, unknown> | null;
  onSuccess?: (result: ApiEnvelope) => void;
  /** Returns a bare thunk instead of wrapping in createAsyncThunk. */
  isApi?: boolean;
  [key: string]: unknown;
}

interface SliceActions {
  setLoadingState: (payload: { key: string; isLoading: boolean }) => unknown;
  setApiData: (payload: Record<string, unknown>) => unknown;
}

export type ThunkArgs = [
  dispatch: (action: unknown) => unknown,
  getState: () => unknown,
  extraArgument: {
    atsHttp: Record<
      string,
      (url: string, config?: Record<string, unknown>) => Promise<ApiEnvelope>
    >;
  },
];

interface CreateApiActionOptions {
  /** The slice's generated action creators. */
  actions: SliceActions;
  /** Extra keys merged into the success dispatch. */
  successPayload?: (result: ApiEnvelope) => Record<string, unknown>;
  /** Extra keys merged into the error dispatch. */
  errorPayload?: (
    error: Error & { status?: number }
  ) => Record<string, unknown>;
  /** Drop a GET's response once a newer GET for the same key has started. */
  trackStaleReads?: boolean;
}

export function replacePathParameters(
  endpoint: string,
  pathParams?: Record<string, unknown> | null
): string {
  if (!pathParams || typeof pathParams !== 'object') {
    // Handle the case where pathParams is undefined, null, or not an object
    return endpoint;
  }

  const regex = new RegExp(`:${Object.keys(pathParams).join('|')}`, 'g'); // Create a global regex
  return endpoint.replace(regex, (match) => String(pathParams[match.slice(1)])); // Use a callback for replacement
}

export const createApiAction = ({
  actions,
  successPayload,
  errorPayload,
  trackStaleReads = false,
}: CreateApiActionOptions) => {
  const latestReadId: Record<string, number> = {};

  return async (
    currentAction: ApiActionConfig,
    config: ApiRequestOptions,
    args: ThunkArgs
  ): Promise<unknown> => {
    // redux-thunk hands over (dispatch, getState, extraArgument); only the first
    // and last are used here.
    const [dispatch, , extraArgument] = args;
    const { atsHttp } = extraArgument;
    const { endpoint, method, reducerName: key, responseKey } = currentAction;
    const {
      requestConfig = {},
      isBackground = false,
      pathParams = {},
      onSuccess,
    } = config;
    const { setLoadingState, setApiData } = actions;

    const isRead = trackStaleReads && method === 'get';
    const readId = isRead
      ? (latestReadId[key] = (latestReadId[key] || 0) + 1)
      : 0;
    const isSuperseded = () => isRead && latestReadId[key] !== readId;

    // If isBackground skip setting loading state to avoid showing loaders
    if (!isBackground) {
      dispatch(setLoadingState({ key, isLoading: true }));
    }
    const updatedEndpoint = replacePathParameters(endpoint, pathParams);
    try {
      const result = await atsHttp[method](updatedEndpoint, {
        ...requestConfig,
      });
      // ats-https resolves 4xx bodies instead of rejecting (so mutations can read field
      // errors), but that means an error body can land in `.data` for read actions — e.g.
      // a 403's own `data: {}` used to crash JobPipeline's `interviewPlanData.filter`.
      // Gated on `responseKey` deliberately: actions that declare one are reads and want
      // the unwrapped payload, while actions without one are mutations whose callers read
      // field errors straight off the envelope.
      if (responseKey && (result?.status ?? 0) >= 400) {
        // Carry status onto the error so the catch block can pass it to
        // consumers (e.g. AtsJobWrapper's 403 → "no access" screen).
        throw Object.assign(
          new Error(
            result?.errors?.join?.(', ') ||
              `Request to ${endpoint} failed with status ${result.status}`
          ),
          { status: result.status }
        );
      }
      const response = responseKey
        ? (result as Record<string, unknown>)[responseKey]
        : result;
      if (isSuperseded()) return response;
      dispatch(
        setApiData({
          key,
          data: response,
          status: result?.status,
          isLoading: false,
          ...(successPayload ? successPayload(result) : null),
        })
      );
      if (typeof onSuccess === 'function') {
        onSuccess(result);
      }
      return response;
    } catch (error) {
      const err = error as Error & { status?: number };
      if (!isSuperseded()) {
        dispatch(
          setApiData({
            key,
            error: err.message,
            isLoading: false,
            ...(errorPayload ? errorPayload(err) : null),
          })
        );
      }
      console.error(`Error fetching data from ${endpoint}:`, error);
      throw new Error(`Failed to fetch data from ${endpoint}`);
    }
  };
};
